import { useContext, useMemo, useState } from 'react';
import Button from '../../../../../components/Button';
import { config } from '../../../../../config/config';
import {
    VOTE_CONFIRMED,
    VOTE_PENDING,
    VOTE_PENDING_GNOSIS,
} from '../../../../../config/constants';
import appContext from '../../../../../context/app/appContext';
import emissionSpoolsContext from '../../../../../context/emissionSpools/emissionSpoolsContext';
import errorContext from '../../../../../context/error/errorContext';
import modalContext from '../../../../../context/modal/modalContext';
import snapshot from '@snapshot-labs/snapshot.js';
import { useWeb3React } from '@web3-react/core';
import useLatestProposal from '../../../../../hooks/web3/vault/useLatestProposal';
import useEmissionSpools from '../../../../../hooks/web3/vault/useEmissionSpools';
import useUserAvailableVotingPower from '../../../../../hooks/web3/vault/useUserAvailableVotingPower';
import useUserVoteForProposal from '../../../../../hooks/web3/vault/useUserVoteForProposal';
import useProposalVotingResult from '../../../../../hooks/web3/vault/useProposalVotingResult';
import { toBigNumber } from '../../../../../web3/utils';
import moment from 'moment';
import { SafeAppConnector } from '@gnosis.pm/safe-apps-web3-react';
import { WalletConnectConnector } from '@web3-react/walletconnect-connector';
import { WalletLinkConnector } from '@web3-react/walletlink-connector';

const EmissionSpoolsVote: React.FC = () => {
    const { totalInputAlloc, userHasVoted, setUserHasVoted } =
        useContext(emissionSpoolsContext);
    const { openModal, closeModal, setModalData } = useContext(modalContext);
    const { setError } = useContext(errorContext);
    const { interfaceSDK } = useContext(appContext);

    const { library, account, connector } = useWeb3React();

    const { latestProposal } = useLatestProposal();

    const { emissionSpools, updateEmissionSpools } = useEmissionSpools();

    const { updateUserVoteForProposal } = useUserVoteForProposal();

    const { proposalVotingResult, updateProposalVotingResult } =
        useProposalVotingResult();

    const { userAvailableVotingPower } = useUserAvailableVotingPower();

    const [isSwissCitizen, setIsSwissCitizen] = useState(false);

    const votingPowerFullyAllocated = useMemo(() => {
        if (!Object.values(totalInputAlloc).length || !userAvailableVotingPower) {
            return true;
        }

        const total = Object.values(totalInputAlloc).reduce((totalAlloc, curr) => {
            return +totalAlloc + +curr;
        }, 0);

        return total === Math.floor(+(userAvailableVotingPower || 0));
    }, [totalInputAlloc, userAvailableVotingPower]);

    const buttonText = useMemo(() => {
        switch (true) {
            case latestProposal && latestProposal?.end < moment().unix():
                return 'Proposal has ended';
            case userHasVoted:
                return 'You have already voted for this proposal';
            case !userAvailableVotingPower ||
                toBigNumber(userAvailableVotingPower).isZero():
                return 'voSPOOL is needed to vote';
            case !votingPowerFullyAllocated:
                return 'Please input full voting power';
            default:
                return 'Vote';
        }
    }, [
        latestProposal,
        userHasVoted,
        votingPowerFullyAllocated,
        userAvailableVotingPower,
    ]);

    const votingDisabled = useMemo(
        () =>
            !votingPowerFullyAllocated ||
            !userAvailableVotingPower ||
            userHasVoted ||
            toBigNumber(userAvailableVotingPower).isZero(),
        [votingPowerFullyAllocated, userAvailableVotingPower, userHasVoted]
    );

    const isGnosisSafe = useMemo(
        () =>
            (connector && connector instanceof SafeAppConnector) ||
            (connector instanceof WalletConnectConnector &&
                Object(connector.walletConnectProvider).signer?.connection.wc.peerMeta
                    .name === 'Gnosis Safe Multisig'),
        [connector]
    );

    const handleVote = async () => {
        try {
            if (!proposalVotingResult || !emissionSpools) return;

            setModalData((prev) => ({
                ...prev,
                message: VOTE_PENDING,
            }));
            openModal('waitingModal');

            const client = isGnosisSafe
                ? new snapshot.Client(config.snapshot.SNAPSHOT_HUB_RELAYER)
                : new snapshot.Client(config.snapshot.SNAPSHOT_HUB_PRODUCTION);

            const client712 = new snapshot.Client712(
                config.snapshot.SNAPSHOT_HUB_PRODUCTION
            );

            const voteObj = Object.entries(totalInputAlloc).reduce(
                (choices, [_, value], index) => {
                    return value
                        ? {
                              ...choices,
                              [index + 1]: +value,
                          }
                        : choices;
                },
                {}
            );

            if (
                (connector &&
                    connector instanceof SafeAppConnector &&
                    (await connector.isSafeApp())) ||
                connector instanceof WalletConnectConnector ||
                connector instanceof WalletLinkConnector
            ) {
                await client.broadcast(
                    library,
                    account!,
                    config.snapshot.SNAPSHOT_SPACE,
                    config.snapshot.SNAPSHOT_PERSONAL_SIGN_VOTE_TYPE,
                    {
                        proposal: latestProposal?.id!,
                        // @ts-ignore
                        choice: voteObj,
                    }
                );
            } else {
                await client712.vote(library, account!, {
                    space: config.snapshot.SNAPSHOT_SPACE,
                    proposal: latestProposal?.id!,
                    type: config.snapshot.SNAPSHOT_VOTE_TYPE,
                    // @ts-ignore
                    choice: voteObj,
                });

                setUserHasVoted(true);

                const emissionSpoolsAllocWeight = emissionSpools.reduce(
                    (accumulatedAlloc, emissionSpool) =>
                        accumulatedAlloc + emissionSpool.allocAmount,
                    0
                );

                const totalVotes = Object.values(proposalVotingResult?.absolute).reduce(
                    (acc, curr) => (acc += curr),
                    0
                );

                await updateUserVoteForProposal(0);

                await updateProposalVotingResult(totalVotes);

                await updateEmissionSpools(emissionSpoolsAllocWeight);
            }

            interfaceSDK?.set.saveUserVotingData(
                account!,
                latestProposal?.id!,
                JSON.stringify({
                    isSwissCitizen: isSwissCitizen,
                    isGnosisSafe: isGnosisSafe,
                })
            );

            setModalData((prev) => ({
                ...prev,
                status: isGnosisSafe ? VOTE_PENDING_GNOSIS : VOTE_CONFIRMED,
                message: '',
            }));
            openModal('successModal');
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <>
            <div className={'citizen-checkbox'}>
                <input
                    type='checkbox'
                    id='swissCitizen'
                    checked={isSwissCitizen}
                    disabled={votingDisabled}
                    onChange={() => {
                        setIsSwissCitizen((prevState) => !prevState);
                    }}
                />
                <label
                    htmlFor='swissCitizen'
                    className={votingDisabled ? 'disabled' : ''}
                >
                    I am a tax resident of Switzerland
                </label>
            </div>
            <Button variant='primary' onClick={handleVote} disabled={votingDisabled}>
                {buttonText}
            </Button>
        </>
    );
};

export default EmissionSpoolsVote;
